import {useCallback, useEffect, useRef, useState} from 'react'
import {useScroll} from 'motion/react'

import {debounce, DebouncedFunc} from '@vio/core/src/utils/debounce'

type ScrollDirection = 'up' | 'down' | null

/**
 * Builds on `useScroll` to expose the current vertical scroll position along
 * with the direction of travel.
 *
 * @param hasDebounce - If true, the scroll handler is debounced (100ms).
 * @param forceStop - If true, scroll updates are ignored and the last values are kept.
 * @returns `scrollYValue` (current scroll position) and `directionY` ('up' | 'down' | null).
 */
export function useEnhancedScroll(hasDebounce?: boolean, forceStop?: boolean) {
  const {scrollY} = useScroll()

  const prevY = useRef(0)
  const [directionY, setDirectionY] = useState<ScrollDirection>(null)
  const [scrollYValue, setScrollYValue] = useState<number>(0)

  // Memoize the scroll handler to prevent unnecessary recreations
  const handleChangeScroll = useCallback(
    (latest: number) => {
      if (forceStop) return
      // Only update if scroll position is not zero (prevents initial state issues)
      if (latest > 0) {
        if (latest > prevY.current) {
          setDirectionY('down')
        } else if (latest < prevY.current) {
          setDirectionY('up')
        }
        setScrollYValue(latest)
      } else {
        setDirectionY(null)
        setScrollYValue(0)
      }
      prevY.current = window ? window.scrollY : latest
    },
    [forceStop] // Add forceStop as a dependency
  )

  useEffect(() => {
    // Keep the last reading while stopped so an overlay opening over a
    // scrolled page doesn't re-expand the header (iOS body-lock also snaps
    // scrollY to 0 here).
    if (forceStop) return
    let handler: (latest: number) => void

    if (hasDebounce) {
      handler = debounce(handleChangeScroll, 100)
    } else {
      handler = handleChangeScroll
    }

    // Subscribe to scrollY changes
    const unsubscribe = scrollY.on('change', handler)

    // Clean up the subscription when the component unmounts or dependencies change
    return () => {
      // Important: if using debounce, ensure to cancel any pending debounced calls
      if (hasDebounce && handler instanceof Function && 'cancel' in handler) {
        ;(handler as DebouncedFunc<typeof handleChangeScroll>).cancel()
      }
      unsubscribe()
    }
  }, [scrollY, hasDebounce, handleChangeScroll, forceStop]) // Add forceStop as a dependency

  return {scrollYValue, directionY}
}
